'use client';
import Link from 'next/link';
import { useState } from 'react';

const ErrorDetails = ({
  err,
}: {
  err?: { message?: string; stack?: string };
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!err?.message && !err?.stack) return null;
  return (
    <div className="mt-6 w-full max-w-2xl">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="py-2.5 px-5 me-2 mb-2 text-sm font-medium text-gray-500 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 inline-flex items-center"
        aria-expanded={isExpanded}
      >
        <svg
          className={`w-3 h-3 mr-1.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M9 18L15 12L9 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Technical Details
      </button>
      {isExpanded && (
        <div className="mt-2 p-3 bg-gray-50/50 rounded-md font-mono text-xs overflow-auto border border-gray-100">
          <div className="text-red-600 font-medium">{err.message}</div>
          {err.stack && (
            <pre className="mt-2 text-gray-600 whitespace-pre-wrap text-[11px]">
              {err.stack}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <html lang="en">
      <body>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-lg">
            <img
              src="/images/ic-error.svg"
              className="w-24 h-24 mx-auto"
              alt="Error 500"
            />
            <h1 className="text-4xl font-bold mt-6 text-gray-800">500</h1>
            <h2 className="mt-2 text-lg text-gray-600">
              An error occurred on server
            </h2>
            <p className="mt-4 text-sm text-gray-500">
              The page you are looking for might have been removed, had its name
              changed, or is temporarily unavailable.
            </p>
            <div className="mt-8 flex justify-center gap-4">
              <button
                onClick={() => reset()}
                className="max-w-[160px] w-full text-center px-4 py-2 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors"
              >
                Try Again
              </button>
              <Link
                href="/"
                className="max-w-[160px] w-full text-center px-4 py-2 rounded-lg border border-amber-500 text-amber-500 font-medium hover:bg-amber-50 transition-colors flex items-center justify-center"
              >
                Return Home
              </Link>
            </div>
            <ErrorDetails err={error} />
            <div className="mt-8 text-sm text-gray-500">
              <p>You might want to check out:</p>
              <div className="mt-4 space-y-2">
                <Link
                  href="/us-en/daily-sky"
                  className="block text-amber-500 hover:underline"
                >
                  Daily SKY Practices
                </Link>
                <Link
                  href="/us-en/courses"
                  className="block text-amber-500 hover:underline"
                >
                  All Courses
                </Link>
                <Link
                  href="https://www.artofliving.org/us-en/contact-us"
                  className="block text-amber-500 hover:underline"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
